import { Injectable, Signal, computed, inject } from '@angular/core';

import { ProjectRole, TenantCapability, TenantRole } from '../models/api.models';
import { ActiveTenantService } from './active-tenant.service';
import { ProjectsService } from './projects.service';
import { CapabilitiesService } from './capabilities.service';

// Numeric rank for ordered comparisons; must match the backend's role-comparison logic exactly.
const TENANT_RANK: Record<TenantRole, number> = {
  member: 1,
  owner:  3,
};

const PROJECT_RANK: Record<ProjectRole, number> = {
  viewer:         1,
  tester:         2,
  developer:      3,
  lead_developer: 4,
  devops:         4, // same rank as lead_developer - orthogonal capability set
  manager:        5,
  owner:          6,
};

// Signal-based UI-gating service for the `*hasTenantRole` / `*hasProjectRole` directives.
// Tenant owners bypass project-role checks (mirrors the backend `RequireProjectRole` short-circuit).
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly activeTenantService = inject(ActiveTenantService);
  private readonly projectsService     = inject(ProjectsService);
  private readonly capabilitiesService = inject(CapabilitiesService);

  /** Returns a `Signal<boolean>` that is `true` when the caller's tenant role rank ≥ `min`'s rank. */
  hasTenantRole(min: TenantRole): Signal<boolean> {
    return computed(() => {
      const role = this.activeTenantService.activeTenantRole();
      if (!role) return false;
      return TENANT_RANK[role] >= TENANT_RANK[min];
    });
  }

  /** Returns true if the caller holds the capability OR is a tenant owner. */
  hasTenantCapability(cap: TenantCapability): Signal<boolean> {
    return computed(() => {
      if (this.activeTenantService.activeTenantRole() === 'owner') return true;
      return this.capabilitiesService.capabilities().includes(cap);
    });
  }

  hasProjectRole(args: { projectId: string; min: ProjectRole }): Signal<boolean> {
    return computed(() => {
      // Tenant owners bypass project-role checks; mirrors the backend middleware short-circuit.
      if (this.activeTenantService.activeTenantRole() === 'owner') return true;
      const role = this.projectsService.roleByProjectId().get(args.projectId);
      if (!role) return false;
      return PROJECT_RANK[role] >= PROJECT_RANK[args.min];
    });
  }
}
